<script setup lang="ts">
import WrAccordion from "~/components/molecules/WrAccordion.vue";
import WrGradientText from "~/components/global/WrGradientText.vue";
import WrButton from "~/components/global/WrButton.vue";

interface FaqItem {
    title: string;
    description: string;
    tags: string[];
}

type FaqThemeColors = Record<string, string>;

const props = withDefaults(
  defineProps<{
    heading: string;
    suffix: string;
    subtext: string;
    filterTags: string[];
    faqItems: FaqItem[];
    ctaText?: string;
    ctaLink?: string | null;
    hideBackground?: boolean;
    /** Render the dark-mode glow blob even when `hideBackground` is set. */
    showGlow?: boolean;
    /**
     * Optional per-theme color palette. Keys are the CSS variables this section
     * (and its heading/accordion children) consume, so the values cascade down:
     *   --text-gradient-1 / --text-gradient-2  heading gradient
     *   --border-accordion / --bg-accordion    accordion item border / fill
     *   --faq-arc-inner / --faq-arc-outer      top arc band
     *   --faq-ring                             filter-tag focus ring
     * Omit any key (or the whole prop) to keep the default brand colors.
     */
    colors?: { light: FaqThemeColors; dark: FaqThemeColors };
    arcGradient?: string;
    activeTagClass?: string;
    ctaAppendClass?: string;
  }>(),
  {
    ctaText: "Connect with us",
    ctaLink: null,
    hideBackground: false,
    showGlow: false,
    activeTagClass: "active",
    ctaAppendClass: "",
    arcGradient:
      "bg-[linear-gradient(0deg,#1F013405_31.11%,#4D27B600_51.33%,#855BF200_57.98%,#22206D20_62.05%)] dark:bg-[linear-gradient(0deg,#1F0134_31.11%,#4D27B600_51.33%,#855BF200_57.98%,#22206D_62.05%)]",
  },
);

const emit = defineEmits<{
    "cta-clicked": [];
}>();

const activeIndex = ref(0);

function setActive(index: number): void {
    activeIndex.value = index;
}

// The section's own accent colors (arc + filter ring) are section-local, so they
// always carry a default matching the original brand look. A passed `colors`
// palette can override these and the cascade vars documented on the prop.
const sectionLocalColors: Record<"light" | "dark", FaqThemeColors> = {
  light: {
    "--faq-arc-inner": "rgba(133, 91, 242, 0.3)",
    "--faq-arc-outer": "rgba(186, 85, 254, 0.25)",
    "--faq-ring": "#BA55FE",
  },
  dark: {
    "--faq-arc-inner": "rgba(133, 91, 242, 0.3)",
    "--faq-arc-outer": "rgba(186, 85, 254, 0.25)",
    "--faq-ring": "#BA55FE",
  },
};

// Palette, emitted as CSS vars on the section root so the heading
// (WrGradientText) and accordion (WrAccordion) inherit them. Each var is emitted
// in a -light and -dark form; the scoped CSS below aliases the base var to the
// light value by default and to the dark value under .my-app-dark. Driving the
// theme from the .my-app-dark class (not a JS color-mode check) keeps it
// SSR-safe: the server can't read the client's sessionStorage theme.
const themeVars = computed<Record<string, string | undefined>>(() => {
  const light = { ...sectionLocalColors.light, ...(props.colors?.light ?? {}) };
  const dark = { ...sectionLocalColors.dark, ...(props.colors?.dark ?? {}) };
  const vars: Record<string, string | undefined> = {};
  new Set([...Object.keys(light), ...Object.keys(dark)]).forEach((key) => {
    if (light[key] != null) vars[`${key}-light`] = light[key];
    if (dark[key] != null) vars[`${key}-dark`] = dark[key];
  });
  return vars;
});

const filteredAccordionContent = computed(() => {
    const selectedTag = props.filterTags[activeIndex.value];
    const items = selectedTag === 'All'
        ? props.faqItems
        : props.faqItems.filter((item) => item.tags.includes(selectedTag));
    return items.map((item) => ({
        title: item.title,
        description: item.description,
    }));
});
</script>

<template>
  <section
    class="relative pb-10 lg:pb-24 overflow-visible grid place-items-center md:mt-12"
    :class="{ 'has-faq-palette': !!props.colors }"
    aria-label="Frequently asked questions"
    :style="themeVars"
  >
    <!-- Dark mode background decoration -->
    <div
      v-if="showGlow || !hideBackground"
      :class="[
        'absolute top-20 left-1/2 -translate-x-1/2 w-[2266px] h-[1241px] rounded-[100%] blur-[100px] pointer-events-none',
        { 'faq-glow': showGlow },
        arcGradient,
      ]"
      aria-hidden="true"
    >
      <div
        class="w-[2260px] h-[1440px] bg-white dark:bg-[var(--color-5)] rounded-[100%] absolute top-[200px] left-0"
      ></div>
    </div>

        <!-- Rising stars decoration -->
        <div class="rising-stars top-0">
            <div style="height: 4px; width: 4px"></div>
            <div style="height: 2px; width: 2px"></div>
            <div style="height: 1px; width: 1px"></div>
        </div>

    <!-- Content -->
    <div
      class="max-w-screen-xl mx-auto px-4 w-full grid md:grid-cols-2 gap-6 relative z-10"
    >
      <!-- Left column: heading, filter tags, CTA -->
      <div class="max-md:text-center mx-auto max-w-[504px]">
        <h2>
          <WrGradientText
            :text="heading"
            append-class="py-0 md:py-4"
          />
          <span>{{ suffix }}</span>
        </h2>
        <div
          class="mt-5 pr-4 text-lg tracking-[0.5px]"
        >{{ subtext }}</div>

        <ul
          class="mt-8 list-disc max-md:max-w-max max-md:mx-auto max-md:grid max-md:grid-cols-2 md:grid-cols-2 max-md:gap-x-6 max-md:gap-y-3 text-[var(--secondary-foreground)] md:space-y-4 text-left pl-4 gap-4"
        >
          <li v-for="(tag, index) in filterTags" :key="tag">
            <button
              type="button"
              @click="setActive(index)"
              :aria-pressed="activeIndex === index"
              :class="[
                'cursor-pointer rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--faq-ring)]',
                { [activeTagClass]: activeIndex === index },
              ]"
            >
              {{ tag }}
            </button>
          </li>
        </ul>
        <WrButton
          container-class="mt-8 max-md:mx-auto"
          :link="ctaLink || undefined"
          :append-class="ctaAppendClass || undefined"
          @click="!ctaLink ? emit('cta-clicked') : undefined"
        >
          {{ ctaText }}
        </WrButton>
      </div>

      <!-- Right column: accordion -->
      <div class="grow">
        <WrAccordion :Content="filteredAccordionContent" />
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Theme is driven by the .my-app-dark class (SSR-safe), not a JS color-mode
   check. Each palette var is emitted inline as -light/-dark; these aliases pick
   the active one. The base vars cascade to the heading (WrGradientText) and
   accordion (WrAccordion) children.
   The aliases ONLY apply when the `colors` prop is provided (`.has-faq-palette`):
   re-declaring a var as an undefined var() (e.g. `--x: var(--x-light)` with no
   -light value) drops the inherited/global value instead of falling back to it,
   which would blank the heading gradient and accordion border/bg on every
   page that themes these vars itself (e.g. genic-ui's dark: overrides). Keys a
   palette omits fall back to the original brand values. */
.has-faq-palette {
  --text-gradient-1: var(--text-gradient-1-light, #12dbff);
  --text-gradient-2: var(--text-gradient-2-light, #ba55fe);
  --border-accordion: var(--border-accordion-light, #4f008833);
  --bg-accordion: var(--bg-accordion-light, linear-gradient(92.4deg, #ffffff 6.22%, #f5f5f5 93.78%));
  --faq-arc-inner: var(--faq-arc-inner-light);
  --faq-arc-outer: var(--faq-arc-outer-light);
  --faq-ring: var(--faq-ring-light);
}
.my-app-dark .has-faq-palette {
  --text-gradient-1: var(--text-gradient-1-dark, #12dbff);
  --text-gradient-2: var(--text-gradient-2-dark, #ba55fe);
  --border-accordion: var(--border-accordion-dark, linear-gradient(91.95deg, #220d42 4.5%, #3d206f 96.91%));
  --bg-accordion: var(--bg-accordion-dark, linear-gradient(93.26deg, #15052e 33.61%, #2a0b55 96.74%));
  --faq-arc-inner: var(--faq-arc-inner-dark);
  --faq-arc-outer: var(--faq-arc-outer-dark);
  --faq-ring: var(--faq-ring-dark);
}

/* Gradient arc across the top of the section - the box is a half-circle
   (flat bottom, curved top) wider than the page, so the arc's legs run off
   both edges and no ends are visible. The radial gradient paints only the
   outer band, so what's visible is just the soft arc, edge to edge. */
.faq-arc {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  width: 200%; /* wider than the page so the arc's legs run off both edges */
  aspect-ratio: 2; /* height = half the width, so the top edge is a true half-circle */
  border-radius: 50% 50% 0 0 / 100% 100% 0 0;
  background: radial-gradient(
    circle farthest-side at 50% 100%,
    transparent 82%,
    var(--faq-arc-inner) 90%,
    var(--faq-arc-outer) 96%,
    transparent 99.5%
  );
  filter: blur(28px);
  pointer-events: none;
}
/* Mobile: a narrower box (smaller radius) so the arc curves visibly across the
   screen instead of reading as a flat line */
@media (max-width: 767px) {
  .faq-arc {
    width: 140%;
  }
}

/* Dark-mode glow blob: hovering the section makes the glow spread a little */
.faq-glow {
  border-radius: 2266px;
  filter: blur(164.75px);
  transition: filter 0.7s ease, transform 0.7s ease;
}

section:hover .faq-glow {
  filter: blur(230px);
  /* spread the gradient outward from its center on hover */
  transform: scale(1.25);
}
</style>
